import React, { useEffect, useState, useCallback } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate } from "react-router-dom";
import {
    me,
    fetchCategories,
    fetchMenuItems,
    fetchContent,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
    createMenuItem,
    updateMenuItem,
    deleteMenuItem,
    reorderMenuItems,
    updateContent,
    changePassword,
    errMsg,
} from "../api";
import { Input, Textarea, Button, Card, ImagePicker, Toast } from "./AdminUI";
import { SortableList, DragHandle } from "./SortableList";

const TABS = [
    { to: "/admin/categories", label: "Categories" },
    { to: "/admin/menu-items", label: "Menu Items" },
    { to: "/admin/hero", label: "Hero" },
    { to: "/admin/hours", label: "Hours" },
    { to: "/admin/location", label: "Location" },
    { to: "/admin/callouts", label: "Callouts" },
    { to: "/admin/favourites", label: "Favourites" },
    { to: "/admin/gallery", label: "Gallery" },
    { to: "/admin/reviews", label: "Reviews" },
    { to: "/admin/patio", label: "Patio" },
    { to: "/admin/featured-in", label: "Featured In" },
    { to: "/admin/general", label: "General" },
    { to: "/admin/burger-sizes", label: "Burger Sizes" },
    { to: "/admin/account", label: "Account" },
];

export const AdminDashboard = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [checking, setChecking] = useState(true);
    const [toast, setToast] = useState({ msg: "", kind: "success" });

    const showToast = (msg, kind = "success") => {
        setToast({ msg, kind });
        setTimeout(() => setToast({ msg: "", kind: "success" }), 2200);
    };

    useEffect(() => {
        const token = localStorage.getItem("strake_token");
        if (!token) {
            navigate("/admin/login", { replace: true });
            return;
        }
        me()
            .then((r) => {
                setUser(r.user);
                setChecking(false);
            })
            .catch(() => {
                localStorage.removeItem("strake_token");
                navigate("/admin/login", { replace: true });
            });
    }, [navigate]);

    if (checking) {
        return <div className="min-h-screen bg-[#1A1410] text-[#F5F2EB] flex items-center justify-center">Checking session…</div>;
    }
    if (!user) return null;

    const logout = () => {
        localStorage.removeItem("strake_token");
        navigate("/admin/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-[#1A1410] text-[#F5F2EB]">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-[#1A1410]/95 backdrop-blur border-b border-[#F5F2EB]/10 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.35em] text-[#D4A437] font-semibold">— Strake Admin</div>
                        <div className="font-display uppercase tracking-tight font-bold text-lg">{user.email}</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <a
                            href="/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] uppercase tracking-[0.3em] text-[#F5F2EB]/75 border-b border-[#D4A437]/50 pb-0.5"
                        >
                            View Site
                        </a>
                        <Button variant="ghost" onClick={logout} testid="admin-logout">Sign Out</Button>
                    </div>
                </div>

                {/* Tab nav — horizontal scroll on mobile */}
                <nav className="mt-3 -mx-4 px-4 overflow-x-auto no-scrollbar">
                    <div className="flex gap-2 min-w-max pb-1">
                        {TABS.map((t) => {
                            const slug = t.to.split("/").pop();
                            return (
                                <NavLink
                                    key={t.to}
                                    to={t.to}
                                    end
                                    data-testid={`admin-tab-${slug}`}
                                    className={({ isActive }) =>
                                        `text-[11px] uppercase tracking-[0.2em] font-display font-bold px-3 py-1.5 border ${
                                            isActive
                                                ? "border-[#D4A437] text-[#D4A437]"
                                                : "border-[#F5F2EB]/15 text-[#F5F2EB]/70"
                                        }`
                                    }
                                >
                                    {t.label}
                                </NavLink>
                            );
                        })}
                    </div>
                </nav>
            </header>

            {/* Body */}
            <main className="px-4 py-5 max-w-2xl mx-auto pb-24">
                <Routes>
                    <Route index element={<Navigate to="categories" replace />} />
                    <Route path="categories" element={<CategoriesTab toast={showToast} />} />
                    <Route path="menu-items" element={<MenuItemsTab toast={showToast} />} />
                    <Route path="hero" element={<SimpleSectionTab section="hero" title="Hero Section" toast={showToast} fields={[
                        { key: "background", type: "image", label: "Hero Background Photo" },
                        { key: "title", label: "Brand Wordmark (large STRAKE text)" },
                        { key: "subtitle", label: "Subtitle (e.g. Gourmet Sandwiches)" },
                        { key: "text", label: "Accent Text (e.g. & Loaded Fries / Tots)" },
                        { key: "kicker", label: "Small Tag (e.g. Behind Treetz Cannabis)" },
                    ]} />} />
                    <Route path="hours" element={<SimpleSectionTab section="hours" title="Hours of Operation" toast={showToast} fields={[
                        { key: "mon_thu", label: "Mon – Thu" },
                        { key: "fri_sat", label: "Fri – Sat" },
                        { key: "sun", label: "Sunday" },
                        { key: "note", label: "Note", type: "textarea" },
                    ]} />} />
                    <Route path="location" element={<SimpleSectionTab section="location" title="Address & Contact" toast={showToast} fields={[
                        { key: "address_line1", label: "Address Line 1" },
                        { key: "address_line2", label: "Address Line 2" },
                        { key: "address_line3", label: "Address Line 3 (postal)" },
                        { key: "address_note", label: "Address Note (e.g. landmark)" },
                        { key: "phone", label: "Phone (display)" },
                        { key: "phone_href", label: "Phone link (tel:...)" },
                        { key: "email", label: "Email" },
                        { key: "instagram", label: "Instagram URL" },
                        { key: "facebook", label: "Facebook URL" },
                        { key: "map_query", label: "Google Maps search query" },
                    ]} />} />
                    <Route path="callouts" element={<CalloutsTab toast={showToast} />} />
                    <Route path="favourites" element={<RepeaterSectionTab section="favourites" title="Customer Favourites" toast={showToast} headerFields={[
                        { key: "kicker", label: "Eyebrow (e.g. The Menu Highlights)" },
                        { key: "heading1", label: "Heading Line 1 (e.g. Customer)" },
                        { key: "heading2", label: "Heading Line 2 (italic, e.g. favourites)" },
                        { key: "subtitle", label: "Subtitle" },
                    ]} itemFields={[
                        { key: "title", label: "Item Title" },
                        { key: "img", label: "Photo", type: "image" },
                        { key: "label", label: "Badge Label (left, e.g. Customer Favourite)" },
                        { key: "tag", label: "Tag (right, e.g. Seafood)" },
                        { key: "desc", label: "Description", type: "textarea" },
                    ]} />} />
                    <Route path="gallery" element={<RepeaterSectionTab section="gallery" title="Gallery (From The Socials)" toast={showToast} sortable headerFields={[
                        { key: "kicker", label: "Eyebrow (e.g. Follow Along)" },
                        { key: "title", label: "Section Heading" },
                        { key: "handle", label: "Handle (e.g. @strakesandwichco)" },
                    ]} itemsKey="photos" itemFields={[
                        { key: "src", label: "Photo", type: "image" },
                        { key: "caption", label: "Caption / Label" },
                    ]} />} />
                    <Route path="reviews" element={<RepeaterSectionTab section="reviews" title="Reviews / Testimonials" toast={showToast} headerFields={[
                        { key: "kicker", label: "Eyebrow (e.g. Locally Loved)" },
                        { key: "heading_line_1", label: "Heading Line 1" },
                        { key: "heading_line_2", label: "Heading Line 2 (italic)" },
                        { key: "heading_line_3", label: "Heading Line 3" },
                        { key: "rating", label: "Overall Rating (e.g. 4.9)" },
                        { key: "rating_label", label: "Rating Label" },
                    ]} itemFields={[
                        { key: "stars", label: "Stars (1–5)", type: "number" },
                        { key: "text", label: "Review Text", type: "textarea" },
                    ]} />} />
                    <Route path="patio" element={<SimpleSectionTab section="patio" title="Patio Section" toast={showToast} fields={[
                        { key: "photo", type: "image", label: "Patio Photo (background)" },
                        { key: "kicker", label: "Eyebrow (e.g. Stay A While)" },
                        { key: "title_line_1", label: "Title Line 1" },
                        { key: "title_line_2", label: "Title Line 2" },
                        { key: "title_line_3", label: "Title Line 3 (italic accent)" },
                        { key: "subtitle", label: "Subtitle" },
                        { key: "body", label: "Body" },
                    ]} />} />
                    <Route path="featured-in" element={<SimpleSectionTab section="featured_in" title="Featured In (Press)" toast={showToast} fields={[
                        { key: "kicker", label: "Top Label (left, e.g. Featured In)" },
                        { key: "side_label", label: "Top Label (right, e.g. Press)" },
                        { key: "publication", label: "Publication Name (e.g. Simcoe.com)" },
                        { key: "publication_meta", label: "Publication Subtitle (e.g. Local Feature)" },
                        { key: "quote", label: "Headline / Pull Quote", type: "textarea" },
                        { key: "supporting", label: "Supporting Text", type: "textarea" },
                        { key: "link_label", label: "Link Text (e.g. Read Article)" },
                        { key: "link_url", label: "Link URL" },
                    ]} />} />
                    <Route path="general" element={<SimpleSectionTab section="general" title="General Site Settings" toast={showToast} fields={[
                        { key: "brand", label: "Brand Name (header)" },
                        { key: "tagline", label: "Tagline / Sub-brand" },
                        { key: "location", label: "Location Text" },
                        { key: "footer", label: "Footer Text" },
                    ]} />} />
                    <Route path="burger-sizes" element={<BurgerSizesTab toast={showToast} />} />
                    <Route path="account" element={<AccountTab toast={showToast} user={user} />} />
                </Routes>
            </main>

            <Toast message={toast.msg} kind={toast.kind} />
        </div>
    );
};

/* ─────────────── Categories ─────────────── */
const CategoriesTab = ({ toast }) => {
    const [cats, setCats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // category object or { _new: true }

    const load = useCallback(async () => {
        try {
            const data = await fetchCategories();
            setCats(data);
        } catch (e) {
            toast(errMsg(e), "error");
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => { load(); }, [load]);

    const save = async (cat) => {
        try {
            if (cat._new) {
                await createCategory({ label: cat.label, sub: cat.sub, photo: cat.photo, pos: cat.pos });
            } else {
                await updateCategory(cat.id, { label: cat.label, sub: cat.sub, photo: cat.photo, pos: cat.pos });
            }
            React.startTransition(() => setEditing(null));
            await load();
            toast("Saved");
        } catch (e) { toast(errMsg(e), "error"); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this category? All menu items inside it will also be deleted.")) return;
        try { await deleteCategory(id); await load(); toast("Deleted"); }
        catch (e) { toast(errMsg(e), "error"); }
    };

    if (loading) return <div className="text-sm text-[#F5F2EB]/60">Loading…</div>;

    const onReorder = async (nextItems, ids) => {
        setCats(nextItems); // optimistic
        try { await reorderCategories(ids); toast("Order saved"); }
        catch (e) { toast(errMsg(e), "error"); await load(); }
    };

    return (
        <div className="space-y-4" data-testid="admin-categories">
            <div className="flex items-center justify-between">
                <h2 className="font-display uppercase font-bold tracking-tight text-2xl">Categories</h2>
                <Button onClick={() => setEditing({ _new: true, label: "", sub: "", photo: "", pos: "center 50%" })} testid="admin-cat-add">+ Add Category</Button>
            </div>

            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4A437]/70 font-body italic">
                Drag the handle on the left to reorder
            </div>

            {editing && (
                <Card title={editing._new ? "New Category" : "Edit Category"}>
                    <CategoryForm value={editing} onChange={setEditing} onSave={save} onCancel={() => setEditing(null)} />
                </Card>
            )}

            <SortableList items={cats} onReorder={onReorder} renderItem={(c, drag) => (
                <Card>
                    <div className="flex items-center gap-1">
                        <DragHandle {...drag} />
                        {c.photo && <img src={c.photo} alt={c.label} className="w-16 h-16 object-cover flex-shrink-0" />}
                        <div className="flex-1 min-w-0 pl-2">
                            <div className="font-display uppercase font-bold tracking-tight text-lg">{c.label}</div>
                            <div className="text-xs text-[#F5F2EB]/60 truncate">{c.sub}</div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4A437]/60">slug: {c.slug || "—"}</div>
                        </div>
                        <div className="flex flex-col gap-1">
                            <Button onClick={() => setEditing(c)}>Edit</Button>
                            <Button variant="danger" onClick={() => del(c.id)}>Delete</Button>
                        </div>
                    </div>
                </Card>
            )} />
        </div>
    );
};

const CategoryForm = ({ value, onChange, onSave, onCancel }) => {
    const set = (k, v) => onChange({ ...value, [k]: v });
    return (
        <div className="space-y-3">
            <Input label="Label" value={value.label} onChange={(v) => set("label", v)} />
            <Input label="Subtitle" value={value.sub} onChange={(v) => set("sub", v)} />
            <ImagePicker label="Card Background Photo" value={value.photo} onChange={(v) => set("photo", v)} />
            <Input label="Photo Position (e.g. center 50%)" value={value.pos} onChange={(v) => set("pos", v)} />
            <div className="flex gap-2 pt-2">
                <Button onClick={() => onSave(value)}>Save</Button>
                <Button variant="ghost" onClick={onCancel}>Cancel</Button>
            </div>
        </div>
    );
};

/* ─────────────── Menu Items ─────────────── */
const MenuItemsTab = ({ toast }) => {
    const [cats, setCats] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        try {
            const [c, i] = await Promise.all([fetchCategories(), fetchMenuItems()]);
            setCats(c);
            setItems(i);
            if (!filter && c.length > 0) setFilter(c[0].id);
        } catch (e) { toast(errMsg(e), "error"); }
        finally { setLoading(false); }
    }, [toast, filter]);

    useEffect(() => { load(); }, [load]);

    const save = async (it) => {
        try {
            if (it._new) {
                await createMenuItem({
                    category_id: it.category_id, name: it.name, desc: it.desc || "",
                    emoji: it.emoji || "", photo: it.photo || "", price: it.price || "",
                });
            } else {
                await updateMenuItem(it.id, {
                    category_id: it.category_id, name: it.name, desc: it.desc,
                    emoji: it.emoji, photo: it.photo, price: it.price,
                });
            }
            React.startTransition(() => setEditing(null));
            await load();
            toast("Saved");
        } catch (e) { toast(errMsg(e), "error"); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this item?")) return;
        try { await deleteMenuItem(id); await load(); toast("Deleted"); }
        catch (e) { toast(errMsg(e), "error"); }
    };

    if (loading) return <div className="text-sm text-[#F5F2EB]/60">Loading…</div>;

    const visible = items.filter((i) => i.category_id === filter)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

    const onReorder = async (nextItems, ids) => {
        // Optimistic — patch sort_order locally
        const idsInOrder = ids;
        const next = items.map((it) => {
            const idx = idsInOrder.indexOf(it.id);
            return idx >= 0 ? { ...it, sort_order: idx + 1 } : it;
        });
        setItems(next);
        try { await reorderMenuItems(idsInOrder); toast("Order saved"); }
        catch (e) { toast(errMsg(e), "error"); await load(); }
    };

    return (
        <div className="space-y-4" data-testid="admin-menu-items">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="font-display uppercase font-bold tracking-tight text-2xl">Menu Items</h2>
                <Button onClick={() => setEditing({ _new: true, category_id: filter, name: "", desc: "", emoji: "", photo: "", price: "" })} testid="admin-item-add">+ Add Item</Button>
            </div>

            {/* Category filter */}
            <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1">
                {cats.map((c) => (
                    <button
                        key={c.id}
                        onClick={() => setFilter(c.id)}
                        className={`text-[11px] uppercase tracking-[0.2em] font-display font-bold px-3 py-1.5 border whitespace-nowrap ${
                            filter === c.id
                                ? "border-[#D4A437] text-[#D4A437]"
                                : "border-[#F5F2EB]/15 text-[#F5F2EB]/70"
                        }`}
                    >
                        {c.label} ({items.filter((i) => i.category_id === c.id).length})
                    </button>
                ))}
            </div>

            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4A437]/70 font-body italic">
                Drag the handle on the left to reorder within this category
            </div>

            {editing && (
                <Card title={editing._new ? "New Item" : "Edit Item"}>
                    <ItemForm value={editing} onChange={setEditing} onSave={save} onCancel={() => setEditing(null)} cats={cats} />
                </Card>
            )}

            <SortableList items={visible} onReorder={onReorder} renderItem={(it, drag) => (
                <Card>
                    <div className="flex items-start gap-1">
                        <DragHandle {...drag} />
                        {it.photo ? (
                            <img src={it.photo} alt={it.name} className="w-16 h-16 object-cover flex-shrink-0" />
                        ) : (
                            <div className="w-16 h-16 bg-[#1A1410] border border-[#F5F2EB]/10 flex-shrink-0 flex items-center justify-center text-[10px] uppercase text-[#F5F2EB]/30">No Photo</div>
                        )}
                        <div className="flex-1 min-w-0 pl-2">
                            <div className="font-display uppercase font-bold tracking-tight text-base">
                                {it.emoji} {it.name}
                            </div>
                            {it.price && <div className="text-[#D4A437] text-sm font-display">{it.price}</div>}
                            <div className="text-xs text-[#F5F2EB]/60 line-clamp-2">{it.desc}</div>
                        </div>
                        <div className="flex flex-col gap-1">
                            <Button onClick={() => setEditing(it)}>Edit</Button>
                            <Button variant="danger" onClick={() => del(it.id)}>Delete</Button>
                        </div>
                    </div>
                </Card>
            )} />

            {visible.length === 0 && !editing && (
                <div className="text-sm text-[#F5F2EB]/50 italic">No items in this category yet.</div>
            )}
        </div>
    );
};

const ItemForm = ({ value, onChange, onSave, onCancel, cats }) => {
    const set = (k, v) => onChange({ ...value, [k]: v });
    return (
        <div className="space-y-3">
            <label className="block">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#D4A437]/90 font-semibold">Category</span>
                <select
                    value={value.category_id || ""}
                    onChange={(e) => set("category_id", e.target.value)}
                    className="mt-1 w-full bg-[#1A1410] text-[#F5F2EB] border border-[#F5F2EB]/15 px-3 py-2 text-sm"
                >
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
            </label>
            <Input label="Emoji (optional, e.g. 🐄 🌶️)" value={value.emoji} onChange={(v) => set("emoji", v)} />
            <Input label="Name" value={value.name} onChange={(v) => set("name", v)} />
            <Textarea label="Description" value={value.desc} onChange={(v) => set("desc", v)} />
            <Input label="Price (e.g. $14)" value={value.price} onChange={(v) => set("price", v)} />
            <ImagePicker label="Item Photo" value={value.photo} onChange={(v) => set("photo", v)} />
            <div className="flex gap-2 pt-2">
                <Button onClick={() => onSave(value)}>Save</Button>
                <Button variant="ghost" onClick={onCancel}>Cancel</Button>
            </div>
        </div>
    );
};

/* ─────────────── Generic site_content section editors ─────────────── */
const useSection = (section) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const reload = useCallback(async () => {
        try {
            const all = await fetchContent();
            setData(all[section] || {});
        } finally { setLoading(false); }
    }, [section]);
    useEffect(() => { reload(); }, [reload]);
    return [data, setData, loading, reload];
};

const SimpleSectionTab = ({ section, title, fields, toast }) => {
    const [data, setData, loading] = useSection(section);
    const [saving, setSaving] = useState(false);

    if (loading || !data) return <div className="text-sm text-[#F5F2EB]/60">Loading…</div>;

    const save = async () => {
        setSaving(true);
        try {
            await updateContent({ [section]: data });
            toast("Saved");
        } catch (e) { toast(errMsg(e), "error"); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-4" data-testid={`admin-section-${section}`}>
            <h2 className="font-display uppercase font-bold tracking-tight text-2xl">{title}</h2>
            <Card>
                <div className="space-y-3">
                    {fields.map((f) => {
                        const v = data[f.key] || "";
                        const set = (val) => setData({ ...data, [f.key]: val });
                        if (f.type === "image") return <ImagePicker key={f.key} label={f.label} value={v} onChange={set} />;
                        if (f.type === "textarea") return <Textarea key={f.key} label={f.label} value={v} onChange={set} />;
                        if (f.type === "number") return <Input key={f.key} type="number" label={f.label} value={v} onChange={set} />;
                        return <Input key={f.key} label={f.label} value={v} onChange={set} />;
                    })}
                </div>
                <div className="mt-4">
                    <Button onClick={save} disabled={saving} testid={`admin-save-${section}`}>{saving ? "Saving…" : "Save Changes"}</Button>
                </div>
            </Card>
        </div>
    );
};

/* Repeater: section with array of items (gallery, reviews, favourites) */
const RepeaterSectionTab = ({ section, title, headerFields = [], itemFields, itemsKey = "items", sortable = false, toast }) => {
    const [data, setData, loading] = useSection(section);
    const [saving, setSaving] = useState(false);

    if (loading || !data) return <div className="text-sm text-[#F5F2EB]/60">Loading…</div>;

    const items = data[itemsKey] || [];
    const setItems = (next) => setData({ ...data, [itemsKey]: next });

    const addItem = () => {
        const blank = Object.fromEntries(itemFields.map((f) => [f.key, ""]));
        setItems([...items, blank]);
    };

    const updateItem = (idx, k, v) => {
        const next = items.slice();
        next[idx] = { ...next[idx], [k]: v };
        setItems(next);
    };

    const removeItem = (idx) => {
        if (!window.confirm("Remove this entry?")) return;
        setItems(items.filter((_, i) => i !== idx));
    };

    const move = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= items.length) return;
        const next = items.slice();
        [next[idx], next[target]] = [next[target], next[idx]];
        setItems(next);
    };

    const save = async () => {
        setSaving(true);
        try { await updateContent({ [section]: data }); toast("Saved"); }
        catch (e) { toast(errMsg(e), "error"); }
        finally { setSaving(false); }
    };

    // Wrap items with stable ids for drag-drop
    const indexed = items.map((it, i) => ({ ...it, id: `${section}-${i}` }));

    const renderEntry = (it, idx, dragProps = null) => (
        <Card key={idx} title={`Entry ${idx + 1}`}>
            <div className="space-y-3">
                {dragProps && (
                    <div className="-mt-1 -ml-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#D4A437]/70 font-body">
                        <DragHandle {...dragProps} />
                        <span>Drag to reorder</span>
                    </div>
                )}
                {itemFields.map((f) => {
                    const v = it[f.key];
                    const set = (val) => updateItem(idx, f.key, val);
                    if (f.type === "image") return <ImagePicker key={f.key} label={f.label} value={v} onChange={set} />;
                    if (f.type === "textarea") return <Textarea key={f.key} label={f.label} value={v} onChange={set} />;
                    if (f.type === "number") return <Input key={f.key} type="number" label={f.label} value={v} onChange={set} />;
                    return <Input key={f.key} label={f.label} value={v} onChange={set} />;
                })}
                <div className="flex gap-2 pt-2">
                    {!dragProps && <Button variant="ghost" onClick={() => move(idx, -1)}>↑ Up</Button>}
                    {!dragProps && <Button variant="ghost" onClick={() => move(idx, 1)}>↓ Down</Button>}
                    <Button variant="danger" onClick={() => removeItem(idx)}>Remove</Button>
                </div>
            </div>
        </Card>
    );

    return (
        <div className="space-y-4" data-testid={`admin-section-${section}`}>
            <h2 className="font-display uppercase font-bold tracking-tight text-2xl">{title}</h2>

            {headerFields.length > 0 && (
                <Card title="Section Header">
                    <div className="space-y-3">
                        {headerFields.map((f) => (
                            <Input key={f.key} label={f.label} value={data[f.key] || ""} onChange={(v) => setData({ ...data, [f.key]: v })} />
                        ))}
                    </div>
                </Card>
            )}

            {sortable ? (
                <SortableList
                    items={indexed}
                    onReorder={(newOrdered) => {
                        // Strip the id we added; preserve original keys only
                        setItems(newOrdered.map(({ id, ...rest }) => rest));
                    }}
                    renderItem={(it, drag) => {
                        const idx = indexed.findIndex((x) => x.id === it.id);
                        return renderEntry(items[idx], idx, drag);
                    }}
                />
            ) : (
                items.map((it, idx) => renderEntry(it, idx))
            )}

            <div className="flex gap-2">
                <Button onClick={addItem}>+ Add Entry</Button>
                <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save All"}</Button>
            </div>
        </div>
    );
};

/* Callouts: side_choice, house_rule, go_loaded, smash_burger_intro */
const CalloutsTab = ({ toast }) => {
    const [data, setData, loading] = useSection("callouts");
    const [saving, setSaving] = useState(false);
    if (loading || !data) return <div className="text-sm text-[#F5F2EB]/60">Loading…</div>;

    const set = (group, key, val) => setData({ ...data, [group]: { ...(data[group] || {}), [key]: val } });
    const setRoot = (key, val) => setData({ ...data, [key]: val });

    const save = async () => {
        setSaving(true);
        try { await updateContent({ callouts: data }); toast("Saved"); }
        catch (e) { toast(errMsg(e), "error"); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-4" data-testid="admin-section-callouts">
            <h2 className="font-display uppercase font-bold tracking-tight text-2xl">Callout Sections</h2>

            <Card title="Side Choice Notice (above category cards)">
                <div className="space-y-3">
                    <Input label="Eyebrow" value={data.side_choice?.eyebrow || ""} onChange={(v) => set("side_choice", "eyebrow", v)} />
                    <Input label="Title" value={data.side_choice?.title || ""} onChange={(v) => set("side_choice", "title", v)} />
                    <Input label="Sub-text" value={data.side_choice?.sub || ""} onChange={(v) => set("side_choice", "sub", v)} />
                </div>
            </Card>

            <Card title="House Rule (orange banner in Loaded Fries)">
                <div className="space-y-3">
                    <Input label="Eyebrow" value={data.house_rule?.eyebrow || ""} onChange={(v) => set("house_rule", "eyebrow", v)} />
                    <Textarea label="Title" value={data.house_rule?.title || ""} onChange={(v) => set("house_rule", "title", v)} />
                </div>
            </Card>

            <Card title="Go Loaded +$8 Combo Upsell (Smash Burgers)">
                <div className="space-y-3">
                    <Input label="Eyebrow" value={data.go_loaded?.eyebrow || ""} onChange={(v) => set("go_loaded", "eyebrow", v)} />
                    <Input label="Title" value={data.go_loaded?.title || ""} onChange={(v) => set("go_loaded", "title", v)} />
                    <Input label="Price" value={data.go_loaded?.price || ""} onChange={(v) => set("go_loaded", "price", v)} />
                    <Textarea label="Body" value={data.go_loaded?.body || ""} onChange={(v) => set("go_loaded", "body", v)} />
                    <ImagePicker label="Combo Photo" value={data.go_loaded?.photo} onChange={(v) => set("go_loaded", "photo", v)} />
                </div>
            </Card>

            <Card title="Smash Burgers — intro line">
                <Textarea label="Intro Description" value={data.smash_burger_intro || ""} onChange={(v) => setRoot("smash_burger_intro", v)} />
            </Card>

            <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save All Callouts"}</Button>
        </div>
    );
};

/* Featured In — list of media outlets */
const FeaturedInTab = ({ toast }) => {
    const [data, setData, loading] = useSection("featured_in");
    const [saving, setSaving] = useState(false);

    if (loading || !data) return <div className="text-sm text-[#F5F2EB]/60">Loading…</div>;

    const items = data.items || [];
    const setItems = (next) => setData({ ...data, items: next });

    const save = async () => {
        setSaving(true);
        try { await updateContent({ featured_in: data }); toast("Saved"); }
        catch (e) { toast(errMsg(e), "error"); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-4" data-testid="admin-section-featured-in">
            <h2 className="font-display uppercase font-bold tracking-tight text-2xl">Featured In</h2>
            <Card>
                <div className="space-y-3">
                    <Input label="Section Title" value={data.title || ""} onChange={(v) => setData({ ...data, title: v })} />
                    {items.map((it, idx) => (
                        <div key={idx} className="flex gap-2 items-end">
                            <div className="flex-1">
                                <Input label={`Outlet ${idx + 1}`} value={it} onChange={(v) => {
                                    const next = items.slice(); next[idx] = v; setItems(next);
                                }} />
                            </div>
                            <Button variant="danger" onClick={() => setItems(items.filter((_, i) => i !== idx))}>×</Button>
                        </div>
                    ))}
                    <Button onClick={() => setItems([...items, ""])}>+ Add Outlet</Button>
                </div>
                <div className="mt-4">
                    <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
                </div>
            </Card>
        </div>
    );
};

/* Burger Sizes */
const BurgerSizesTab = ({ toast }) => {
    const [content, setContent] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchContent().then((c) => setContent(c.burger_sizes || []));
    }, []);

    if (content === null) return <div className="text-sm text-[#F5F2EB]/60">Loading…</div>;

    const items = content;
    const setItems = (next) => setContent(next);
    const upd = (idx, k, v) => { const next = items.slice(); next[idx] = { ...next[idx], [k]: v }; setItems(next); };

    const save = async () => {
        setSaving(true);
        try { await updateContent({ burger_sizes: items }); toast("Saved"); }
        catch (e) { toast(errMsg(e), "error"); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-4" data-testid="admin-section-burger-sizes">
            <h2 className="font-display uppercase font-bold tracking-tight text-2xl">Burger Sizes</h2>
            {items.map((it, idx) => (
                <Card key={idx} title={`Size ${idx + 1}`}>
                    <div className="space-y-3">
                        <Input label="Name" value={it.name} onChange={(v) => upd(idx, "name", v)} />
                        <Input label="Weight" value={it.weight} onChange={(v) => upd(idx, "weight", v)} />
                        <Input label="Note (e.g. one patty)" value={it.note} onChange={(v) => upd(idx, "note", v)} />
                        <Input label="Sub-note (optional, e.g. Not For The Weak 🔥)" value={it.subnote || ""} onChange={(v) => upd(idx, "subnote", v)} />
                        <Button variant="danger" onClick={() => setItems(items.filter((_, i) => i !== idx))}>Remove</Button>
                    </div>
                </Card>
            ))}
            <div className="flex gap-2">
                <Button onClick={() => setItems([...items, { name: "", weight: "", note: "", subnote: "" }])}>+ Add Size</Button>
                <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save All"}</Button>
            </div>
        </div>
    );
};

/* Account — change password */
const AccountTab = ({ toast, user }) => {
    const [current, setCurrent] = useState("");
    const [next1, setNext1] = useState("");
    const [next2, setNext2] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (next1 !== next2) { toast("Passwords don't match", "error"); return; }
        if (next1.length < 8) { toast("Password must be at least 8 characters", "error"); return; }
        setSaving(true);
        try {
            await changePassword(current, next1);
            toast("Password updated");
            setCurrent(""); setNext1(""); setNext2("");
        } catch (e) { toast(errMsg(e), "error"); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-4" data-testid="admin-section-account">
            <h2 className="font-display uppercase font-bold tracking-tight text-2xl">Account</h2>
            <Card title="Logged in as">
                <div className="font-display uppercase tracking-tight">{user.email}</div>
            </Card>
            <Card title="Change Password">
                <div className="space-y-3">
                    <Input type="password" label="Current Password" value={current} onChange={setCurrent} />
                    <Input type="password" label="New Password" value={next1} onChange={setNext1} />
                    <Input type="password" label="Confirm New Password" value={next2} onChange={setNext2} />
                    <Button onClick={submit} disabled={saving}>{saving ? "Saving…" : "Update Password"}</Button>
                </div>
            </Card>
        </div>
    );
};
